import React from "react";
import { Card, CardTitle, CardBody, RightArrowLink } from "components/ui/Card";
import { fetchFinaidAwards } from "data/slices/users/finaid/finaidAwards";
import { Spinner } from "components/ui/Spinner";
import StyledMessage from "components/ui/StyledMessage";
import { UnorderedIconList } from "components/ui/Lists";
import { LargeSpacer } from "components/ui/VerticalSpacers";
import shouldShowDecimals from "assets/javascripts/angular/controllers/widgets/finaid/shouldShowDecimals";
import { AwardComparisonLink } from "./FinancialAidAwardsCard/AwardComparisonLink";
import { FinaidSummaryLink } from "./FinancialAidAwardsCard/FinaidSummaryLink";
import { GiftAid } from "./FinancialAidAwardsCard/GiftAid";
import { SectionByType } from "./FinancialAidAwardsCard/SectionByType";
import { GrandTotal } from "./FinancialAidAwardsCard/GrandTotal";
import { LoanLinks } from "./FinancialAidAwardsCard/LoanLinks";
import { LoansHeader } from "./FinancialAidAwardsCard/LoansHeader";
import { useFinaidYear } from "components/hooks/useFinaidYear";
import { dispatchUserFinaidSlice } from "components/hooks/dispatchUserFinaidSlice";
import { useFinaidYearId } from "components/hooks/useFinaidYearId";

// https://wikihub.berkeley.edu/display/SIS/CalCentral+Financial+Aid+Awards+Card
export function FinancialAidAwardsCard() {
  dispatchUserFinaidSlice(fetchFinaidAwards);

  const finaidYearId = useFinaidYearId();
  const finaidAwards = useFinaidYear(({ finaidAwards }) => finaidAwards) || {};
  const {
    loadState,
    messages: { messageInfo } = {},
    awards: {
      hasLoans,
      grandtotal: { total: { amount: grandTotalAmount } = {} } = {},
    } = {},
  } = finaidAwards;

  const showDecimals = grandTotalAmount > 0 && shouldShowDecimals(finaidAwards);

  return (
    <Card data-testid="finaid-awards-card">
      <CardTitle
        title="Awards"
        right={
          grandTotalAmount > 0 && (
            <RightArrowLink to={`/finances/finaid/awards/${finaidYearId}`}>
              View By Term
            </RightArrowLink>
          )
        }
      />

      {loadState === "pending" && (
        <CardBody>
          <Spinner />
        </CardBody>
      )}

      {loadState === "success" && grandTotalAmount > 0 && (
        <>
          <CardBody style={{ paddingBottom: `0` }}>
            <StyledMessage style={{ marginTop: `0` }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: messageInfo,
                }}
              ></div>
            </StyledMessage>
            <LargeSpacer />
            <UnorderedIconList style={{ color: `var(--dove-gray)` }}>
              <FinaidSummaryLink />
              <AwardComparisonLink />
            </UnorderedIconList>
          </CardBody>

          <GiftAid showDecimals={showDecimals} />
          <SectionByType
            type="waiversAndOther"
            color="regent-st-blue"
            showDecimals={showDecimals}
          />
          <SectionByType
            type="workstudy"
            color="regent-st-blue"
            showDecimals={showDecimals}
          />

          {hasLoans && (
            <>
              <LoansHeader>Loans</LoansHeader>
              <LoanLinks />

              <SectionByType
                type="subsidizedloans"
                color="wedgewood"
                showDecimals={showDecimals}
              />
              <SectionByType
                type="unsubsidizedloans"
                color="wedgewood"
                showDecimals={showDecimals}
              />
              <SectionByType
                type="alternativeloans"
                color="wedgewood"
                showDecimals={showDecimals}
              />
              <SectionByType
                type="plusloans"
                color="wedgewood"
                showDecimals={showDecimals}
              />
            </>
          )}

          <GrandTotal showDecimals={showDecimals} />
        </>
      )}

      {loadState === "success" && !grandTotalAmount && (
        <CardBody>
          <div>
            There is currently no awards information available for this aid
            year.
          </div>
        </CardBody>
      )}
    </Card>
  );
}
